/**
 * Lab display utilities.
 *
 * Resolves human-readable panel names from orders that may only carry
 * provider IDs or UUIDs in the `panel_name` / `panel.name` fields.
 */

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Returns true when a string looks like a raw UUID and should NOT be
 * shown to users as a panel name.
 */
export const isRawId = (value: string | null | undefined): boolean => {
  if (!value) return false
  return UUID_RE.test(value.trim())
}

/**
 * Maps treatment_area codes to human-readable panel names.
 * Used as a last-resort fallback when no proper panel name is available.
 */
const TREATMENT_AREA_LABELS: Record<string, string> = {
  HRT_MEN: "Men's Hormone Panel",
  HRT_WOMEN: "Women's Hormone Panel",
  NIMCORE_MEN: "NimCore Men's Panel",
  NIMCORE_WOMEN: "NimCore Women's Panel",
  WEIGHT_LOSS: 'Weight Management Panel',
  HAIR_LOSS: 'Hair Loss Panel',
  GENERAL: 'General Health Panel',
}

/**
 * Resolve a human-readable panel name from an order-like object.
 *
 * Evaluation order:
 * 1. `provider_metadata.lab_test.name`
 * 2. `provider_metadata.lab_tests[0].display_name || name`
 * 3. `panel.name`
 * 4. `panel_name`
 * 5. `panel.treatmentArea` or `panel.treatment_area` mapped to label
 *
 * Any value that looks like a raw UUID is skipped.
 *
 * @param order  Partial lab order (or any object with the relevant fields)
 * @param fallback  Fallback text when no name is found (default: 'Lab Panel')
 */
export const resolvePanelName = (
  order: {
    panel_name?: string | null
    panel?: {
      name?: string | null
      treatmentArea?: string | null
      treatment_area?: string | null
    } | null
    provider_metadata?: {
      lab_test?: { name?: string | null } | null
      lab_tests?: Array<{ name?: string; display_name?: string }> | null
    } | null
  } | null | undefined,
  fallback = 'Lab Panel',
): string => {
  if (!order) return fallback

  const candidates: (string | null | undefined)[] = [
    order.provider_metadata?.lab_test?.name,
    order.provider_metadata?.lab_tests?.[0]?.display_name ??
      order.provider_metadata?.lab_tests?.[0]?.name,
    order.panel?.name,
    order.panel_name,
  ]

  for (const c of candidates) {
    if (c && !isRawId(c)) return c
  }

  // Last resort: map treatment_area to a readable label
  const area = order.panel?.treatmentArea ?? order.panel?.treatment_area
  if (area && TREATMENT_AREA_LABELS[area]) {
    return TREATMENT_AREA_LABELS[area]
  }

  return fallback
}
